import { useEffect, useMemo, useState } from "react";
import DOMPurify from "dompurify";
import { adminGetPRD } from "@/lib/api";
import { FileText, Loader2, ChevronLeft, ChevronRight } from "lucide-react";

const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
const inline = (s) =>
  esc(s)
    .replace(/`([^`]+)`/g, '<code class="rounded bg-parchment px-1.5 py-0.5 font-mono text-[0.85em] text-saffron">$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="text-ink">$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, '<a href="$2" target="_blank" rel="noreferrer" class="text-saffron underline">$1</a>');

// Minimal markdown → HTML for the recorded PRD/Changelog/Roadmap notes.
function mdToHtml(md) {
  const lines = (md || "").replace(/\r\n/g, "\n").split("\n");
  const out = [];
  let list = null; // current list indent stack not needed — flatten
  const closeList = () => { if (list) { out.push("</ul>"); list = null; } };
  for (let raw of lines) {
    const line = raw.replace(/\t/g, "    ");
    const trimmed = line.trim();
    if (!trimmed) { closeList(); continue; }
    if (/^#{1,6}\s/.test(trimmed)) {
      closeList();
      const level = trimmed.match(/^#+/)[0].length;
      const text = inline(trimmed.replace(/^#{1,6}\s/, ""));
      const cls = level === 1 ? "mt-8 mb-3 font-serif-latin text-2xl text-ink"
        : level === 2 ? "mt-8 mb-2.5 border-t border-line pt-6 font-serif-latin text-xl text-ink"
        : "mt-5 mb-1.5 font-serif-latin text-base font-semibold text-ink";
      out.push(`<h${Math.min(level, 4)} class="${cls}">${text}</h${Math.min(level, 4)}>`);
      continue;
    }
    if (/^(-{3,}|\*{3,}|_{3,})$/.test(trimmed)) { closeList(); out.push('<hr class="my-6 border-line" />'); continue; }
    const bullet = trimmed.match(/^[-*]\s+(.*)$/);
    if (bullet) {
      const indent = line.match(/^\s*/)[0].length;
      if (!list) { out.push('<ul class="my-2 space-y-1.5">'); list = true; }
      const pad = indent >= 2 ? ' style="margin-left:1.25rem"' : "";
      out.push(`<li${pad} class="relative pl-4 text-[15px] leading-relaxed text-ink-2 before:absolute before:left-0 before:top-[0.6em] before:h-1.5 before:w-1.5 before:-translate-y-1/2 before:rotate-45 before:bg-[var(--gold)]">${inline(bullet[1])}</li>`);
      continue;
    }
    closeList();
    out.push(`<p class="my-2 text-[15px] leading-relaxed text-ink-2">${inline(trimmed)}</p>`);
  }
  closeList();
  return DOMPurify.sanitize(out.join("\n"), {
    ALLOWED_TAGS: ["h1", "h2", "h3", "h4", "p", "ul", "li", "strong", "em", "code", "a", "hr", "br"],
    ALLOWED_ATTR: ["class", "style", "href", "target", "rel"],
  });
}

export default function AdminPRD() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [active, setActive] = useState(0);

  useEffect(() => {
    adminGetPRD().then(setData).catch((e) => setErr(e.response?.data?.detail || e.message));
  }, []);

  const files = data?.files || [];
  const current = files[active];
  const html = useMemo(() => (current ? mdToHtml(current.content) : ""), [current]);

  return (
    <div data-testid="admin-prd-page">
      <div className="mb-4">
        <h1 className="font-serif-latin text-2xl text-ink">Product Notes (PRD)</h1>
        <p className="mt-1 text-sm text-ink-3">The recorded product requirements, changelog and roadmap for Dharmartha — everything built so far, kept for reference.</p>
      </div>

      {err && <p className="border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="prd-error">Couldn't load the notes: {err}</p>}

      {!data && !err && (
        <p className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading notes…</p>
      )}

      {data && files.length === 0 && !err && (
        <p className="py-16 text-center text-ink-3" data-testid="prd-empty">No product notes have been recorded yet.</p>
      )}

      {files.length > 0 && (
        <>
          {files.length > 1 && (
            <div className="mb-5 flex flex-wrap items-center justify-between gap-3 border-b border-line pb-3">
              <div className="flex flex-wrap items-center gap-2">
                {files.map((f, i) => (
                  <button key={f.name} onClick={() => setActive(i)} data-testid={`prd-tab-${f.name}`}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm ${i === active ? "border-b-2 border-saffron text-saffron" : "text-ink-2 hover:text-ink"}`}>
                    <FileText className="h-4 w-4" /> {f.name.replace(/\.md$/i, "")}
                  </button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <button data-testid="prd-prev" onClick={() => setActive((i) => Math.max(0, i - 1))} disabled={active <= 0}
                  className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-40">
                  <ChevronLeft className="h-4 w-4" /> Prev
                </button>
                <span className="text-xs text-ink-3">{active + 1} / {files.length}</span>
                <button data-testid="prd-next" onClick={() => setActive((i) => Math.min(files.length - 1, i + 1))} disabled={active >= files.length - 1}
                  className="inline-flex items-center gap-1.5 border border-line px-3 py-1.5 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron disabled:opacity-40">
                  Next <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          )}
          <article className="max-w-3xl border border-line bg-surface p-6 md:p-8" data-testid="prd-content"
            dangerouslySetInnerHTML={{ __html: html }} />
        </>
      )}
    </div>
  );
}
